import { API_BASE_URL } from "@/lib/appEnvironment";
import { getActiveAccessToken } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { storagePathFromValue } from "@/lib/storageFetch";

export type EntityType =
  | "activities"
  | "sellers"
  | "activity_items"
  | "support-tickets"
  | "carousel-slides";

export type StorageUploadPhase = "uploading" | "processing";

export type StorageUploadProgress = {
  progress: number;
  phase: StorageUploadPhase;
};

export type StorageUploadProgressCallback = (
  state: StorageUploadProgress,
) => void;

export type UploadFileOptions = {
  onProgress?: StorageUploadProgressCallback;
};

interface StorageUploadResponse {
  path: string;
  file_url: string;
}

interface DeleteFileResponse {
  message: string;
}

const UPLOAD_PROGRESS_MAX = 85;
const UPLOAD_PROCESSING_PROGRESS = 92;

export class StorageUploadError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "StorageUploadError";
    this.status = status;
  }
}

function buildUploadUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  return `${base}/storage/upload`;
}

function throttleProgress(
  onProgress?: StorageUploadProgressCallback,
): StorageUploadProgressCallback | undefined {
  if (!onProgress) {
    return undefined;
  }
  let lastProgress = -1;
  return (state) => {
    if (
      state.phase === "processing" ||
      state.progress === 100 ||
      state.progress - lastProgress >= 1
    ) {
      lastProgress = state.progress;
      onProgress(state);
    }
  };
}

function uploadForm(
  form: FormData,
  onProgress?: StorageUploadProgressCallback,
): Promise<StorageUploadResponse> {
  const emitProgress = throttleProgress(onProgress);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUploadUrl());
    xhr.withCredentials = true;
    const token = getActiveAccessToken();
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }

    let reportedProcessing = false;
    const reportProcessing = () => {
      if (reportedProcessing) {
        return;
      }
      reportedProcessing = true;
      emitProgress?.({
        progress: UPLOAD_PROCESSING_PROGRESS,
        phase: "processing",
      });
    };

    xhr.upload.onprogress = (event) => {
      if (!emitProgress || !event.lengthComputable) {
        return;
      }
      if (event.loaded >= event.total) {
        reportProcessing();
        return;
      }
      emitProgress({
        progress: Math.round((event.loaded / event.total) * UPLOAD_PROGRESS_MAX),
        phase: "uploading",
      });
    };

    xhr.onload = () => {
      emitProgress?.({ progress: 100, phase: "processing" });

      if (xhr.status < 200 || xhr.status >= 300) {
        let detail: string | undefined;
        try {
          const body = JSON.parse(xhr.responseText) as {
            detail?: string;
            message?: string;
          };
          detail = body.message ?? body.detail;
        } catch {
          detail = xhr.responseText || undefined;
        }
        reject(
          new StorageUploadError(
            detail ?? `Upload failed (${xhr.status})`,
            xhr.status,
          ),
        );
        return;
      }

      try {
        resolve(JSON.parse(xhr.responseText) as StorageUploadResponse);
      } catch {
        reject(new StorageUploadError("Upload failed: invalid response"));
      }
    };

    xhr.onerror = () => {
      reject(new StorageUploadError("Upload failed"));
    };

    xhr.send(form);
  });
}

export const storageService = {
  uploadFile: async (
    entityType: EntityType,
    entityId: string,
    file: File,
    options?: UploadFileOptions,
  ): Promise<string> => {
    const form = new FormData();
    form.append("entity_type", entityType);
    form.append("entity_id", entityId);
    form.append("file", file);
    const data = await uploadForm(form, options?.onProgress);
    return data.file_url;
  },

  deleteFile: async (fileUrlOrPath: string): Promise<DeleteFileResponse> => {
    const response = await apiClient.delete<DeleteFileResponse>(
      "/storage/file",
      {
        data: { storage_path: storagePathFromValue(fileUrlOrPath) },
      },
    );
    return response.data;
  },
};
